// Animatie leren: oude After Effects- en Illustrator-bestanden per klant uploaden en
// daar een werkwijze (skill) uit halen die Astra meekrijgt bij een nieuwe animatie.
//
// Joris, 13 sep 2026: "maak het mogelijk om in intern oude AE bestanden en AI bestanden te
// uploaden en daarvan te leren. Liefst learnings per klant." Plan: docs/astra-animatieflow-plan.md §16.
// De analyse van .aep/.ai gebeurt op de Mac Studio (worker); tot die draait staan bronnen op
// "wacht op analyse" en is de skill handmatig of uit Astra's voorstel te vullen.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Paper, TextField,
  Tooltip, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { leesJson } from '../../utils/apiFout';

const API = `${BACKEND_URL}/Api/animatie`;
const TOEGESTAAN = ['.aep', '.aepx', '.ai', '.psd', '.mp4', '.mov', '.zip', '.pdf'];

const SOORT = {
  aep: { label: 'After Effects', color: 'secondary' },
  ai: { label: 'Illustrator', color: 'warning' },
  psd: { label: 'Photoshop', color: 'default' },
  film: { label: 'Eindfilm', color: 'primary' },
  zip: { label: 'Assets (zip)', color: 'default' },
  storyboard: { label: 'Storyboard', color: 'default' },
  overig: { label: 'Overig', color: 'default' },
};
const STATUS = {
  nieuw: { label: 'Wacht op analyse', color: 'default' },
  geanalyseerd: { label: 'Geanalyseerd', color: 'success' },
  mislukt: { label: 'Analyse mislukt', color: 'error' },
};

const mb = (n) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.max(0.1, n / 1e6).toFixed(1)} MB`);
const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '');

export default function AnimatieLeren() {
  const [klanten, setKlanten] = useState([]);
  const [klant, setKlant] = useState(null);
  const [bronnen, setBronnen] = useState([]);
  const [laden, setLaden] = useState(false);
  const [uploadt, setUploadt] = useState(null); // {gedaan, totaal, naam}
  const [sleept, setSleept] = useState(false);
  const [projectNumber, setProjectNumber] = useState('');
  const [notitie, setNotitie] = useState('');
  const [skill, setSkill] = useState(null);      // {skillMd, versie, bron, historie}
  const [skillTekst, setSkillTekst] = useState('');
  const [voorstellen, setVoorstellen] = useState([]);
  const [opslaan, setOpslaan] = useState(false);
  const [analyseert, setAnalyseert] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await leesJson(await authFetch(`${BACKEND_URL}/Api/klanten`), 'Klanten laden');
        const lijst = (d.klanten || []).map((k) => ({
          id: k.id, naam: k.fields?.Bedrijfsnaam || k.fields?.Naam || k.naam || k.id,
        })).sort((a, b) => a.naam.localeCompare(b.naam, 'nl'));
        setKlanten(lijst);
      } catch (e) { toast.error(e.message); }
      try {
        const v = await leesJson(await authFetch(`${API}/skills/voorstellen`), 'Voorstellen laden');
        setVoorstellen(v.voorstellen || []);
      } catch { /* geen voorstellen is geen fout */ }
    })();
  }, []);

  const laad = useCallback(async (k) => {
    if (!k) { setBronnen([]); setSkill(null); setSkillTekst(''); return; }
    setLaden(true);
    try {
      const [b, s] = await Promise.all([
        leesJson(await authFetch(`${API}/bronnen?klant_id=${encodeURIComponent(k.id)}`), 'Bronnen laden'),
        leesJson(await authFetch(`${API}/skills/${encodeURIComponent(k.id)}`), 'Skill laden'),
      ]);
      setBronnen(b.bronnen || []);
      setSkill(s.skill || null);
      setSkillTekst(s.skill?.skillMd || '');
    } catch (e) { toast.error(e.message); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(klant); }, [klant, laad]);

  const uploadBestanden = async (files) => {
    if (!klant) { toast.error('Kies eerst een klant'); return; }
    const lijst = Array.from(files || []).filter((f) => TOEGESTAAN.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (!lijst.length) { toast.error(`Alleen ${TOEGESTAAN.join(', ')}`); return; }
    let gedaan = 0;
    for (const f of lijst) {
      setUploadt({ gedaan, totaal: lijst.length, naam: f.name });
      const fd = new FormData();
      fd.append('file', f);
      fd.append('klantId', klant.id);
      fd.append('klantNaam', klant.naam);
      if (projectNumber.trim()) fd.append('projectNumber', projectNumber.trim());
      if (notitie.trim()) fd.append('notitie', notitie.trim());
      try {
        const r = await authFetch(`${API}/bronnen`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
        gedaan += 1;
      } catch (e) { toast.error(`${f.name}: ${e.message}`); }
    }
    setUploadt(null);
    if (gedaan) toast.success(`${gedaan} bestand${gedaan === 1 ? '' : 'en'} geüpload voor ${klant.naam}`);
    laad(klant);
  };

  const verwijder = async (b) => {
    const ok = await bevestig({
      titel: `"${b.bestandsnaam}" verwijderen?`,
      tekst: 'Het bestand en de analyse ervan verdwijnen uit de leerbibliotheek. De skill blijft staan.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      const r = await authFetch(`${API}/bronnen/${b.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setBronnen((oud) => oud.filter((x) => x.id !== b.id));
      toast.success('Verwijderd');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };

  const bewaarSkill = async (bron = 'mens') => {
    if (!klant) return;
    setOpslaan(true);
    try {
      const r = await authFetch(`${API}/skills/${encodeURIComponent(klant.id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skillMd: skillTekst, klantNaam: klant.naam, bron }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setSkill(d.skill);
      toast.success(`Skill opgeslagen (versie ${d.skill.versie})`);
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
    finally { setOpslaan(false); }
  };

  const importeerVoorstel = async (naam) => {
    try {
      const d = await leesJson(await authFetch(`${API}/skills/voorstellen/${encodeURIComponent(naam)}`), 'Voorstel laden');
      const ok = skillTekst.trim()
        ? await bevestig({ titel: 'Huidige tekst vervangen?', tekst: `De skill-tekst wordt vervangen door het voorstel "${naam}". De vorige versie blijft in de historie.`, knop: 'Vervangen' })
        : true;
      if (!ok) return;
      setSkillTekst(d.skillMd || '');
      toast('Voorstel geladen — bekijk en sla op', { icon: '📝' });
    } catch (e) { toast.error(e.message); }
  };

  const startAnalyse = async () => {
    if (!klant) return;
    const nieuw = bronnen.filter((b) => b.status === 'nieuw' && ['aep', 'ai', 'psd', 'film', 'zip'].includes(b.soort));
    const ok = await bevestig({
      titel: `Astra laten leren van ${nieuw.length} bestand${nieuw.length === 1 ? '' : 'en'}?`,
      tekst: 'Er komt een analyse-job in de wachtrij van de Studio. De animator start Astra daar; het resultaat (learnings) kun je daarna overnemen in de skill van deze klant.',
      knop: 'In de wachtrij zetten',
    });
    if (!ok) return;
    setAnalyseert(true);
    try {
      const r = await authFetch(`${API}/bronnen/analyseer`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ klantId: klant.id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || d));
      toast.success(`Analyse-job ${d.job.id} staat in de wachtrij — zie Animatie-jobs`);
    } catch (e) { toast.error(`Analyse starten mislukt: ${e.message}`); }
    finally { setAnalyseert(false); }
  };

  const gewijzigd = useMemo(() => (skill?.skillMd || '') !== skillTekst, [skill, skillTekst]);
  const perSoort = useMemo(() => bronnen.reduce((acc, b) => ({ ...acc, [b.soort]: (acc[b.soort] || 0) + 1 }), {}), [bronnen]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="school"
        title="Animatie leren"
        subtitle="Upload oude After Effects- en Illustrator-bestanden per klant. Daar leert Astra de werkwijze van de animator uit: wat beweegt, hoe, en in welk tempo."
        accent={{ color: '#635983', bg: '#dbcdff' }}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3, mt: 1 }}>
        {/* Links: klant + upload + bronnen */}
        <Box>
          <Autocomplete
            options={klanten} value={klant} onChange={(_, v) => setKlant(v)}
            getOptionLabel={(o) => o?.naam || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Klant" placeholder="Bijv. Enexis" size="small" />}
            sx={{ mb: 2 }}
          />

          <Paper variant="outlined"
            onDragOver={(e) => { e.preventDefault(); if (klant) setSleept(true); }}
            onDragLeave={() => setSleept(false)}
            onDrop={(e) => { e.preventDefault(); setSleept(false); uploadBestanden(e.dataTransfer?.files); }}
            sx={{
              p: 3, textAlign: 'center', borderStyle: 'dashed', borderWidth: 2, mb: 2,
              borderColor: sleept ? 'primary.main' : 'divider', bgcolor: sleept ? 'action.hover' : 'transparent',
              opacity: klant ? 1 : 0.6, transition: 'all .15s',
            }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
              {klant ? `Sleep bestanden van ${klant.naam} hierheen` : 'Kies eerst een klant'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              After Effects (.aep), Illustrator (.ai), eindfilm (.mp4/.mov), assets (.zip), storyboard (.pdf). Meerdere tegelijk mag.
            </Typography>
            <Button variant="outlined" component="label" disabled={!klant || Boolean(uploadt)} sx={{ textTransform: 'none' }}>
              Bestanden kiezen
              <input hidden type="file" multiple accept={TOEGESTAAN.join(',')} onChange={(e) => { uploadBestanden(e.target.files); e.target.value = ''; }} />
            </Button>
            {uploadt && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Uploaden {uploadt.gedaan + 1} van {uploadt.totaal}: {uploadt.naam}</Typography>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField size="small" label="Projectnummer (optioneel)" value={projectNumber} onChange={(e) => setProjectNumber(e.target.value)} sx={{ width: 220 }} />
            <TextField size="small" label="Notitie bij deze upload (optioneel)" value={notitie} onChange={(e) => setNotitie(e.target.value)} fullWidth
              placeholder="Bijv. 'dit is de finale versie, animator Jelle'" />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">Bronnen{klant ? ` van ${klant.naam}` : ''}</Typography>
            {Object.entries(perSoort).map(([s, n]) => (
              <Chip key={s} size="small" variant="outlined" label={`${SOORT[s]?.label || s} · ${n}`} />
            ))}
            {laden && <CircularProgress size={14} />}
          </Box>
          {klant && !laden && bronnen.length === 0 && (
            <Typography variant="body2" color="text.secondary">Nog niets geüpload voor deze klant.</Typography>
          )}
          {bronnen.map((b) => (
            <Box key={b.id} sx={{
              display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto auto', alignItems: 'center', gap: 1.5,
              py: 1, borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap title={b.bestandsnaam}>{b.bestandsnaam}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {mb(b.bytes)} · {datum(b.op)}{b.projectNumber ? ` · #${b.projectNumber}` : ''}{b.geuploadDoor ? ` · ${b.geuploadDoor.split('@')[0]}` : ''}
                  {b.notitie ? ` · ${b.notitie}` : ''}
                </Typography>
              </Box>
              <Chip size="small" label={SOORT[b.soort]?.label || b.soort} color={SOORT[b.soort]?.color || 'default'} variant="outlined" />
              <Chip size="small" label={STATUS[b.status]?.label || b.status} color={STATUS[b.status]?.color || 'default'} />
              <Tooltip title="Verwijderen"><IconButton size="small" onClick={() => verwijder(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
          ))}
          {bronnen.some((b) => b.status === 'nieuw') && (
            <Alert severity="info" sx={{ mt: 2 }} action={(
              <Button size="small" variant="contained" onClick={startAnalyse} disabled={analyseert} sx={{ textTransform: 'none' }}>
                {analyseert ? <CircularProgress size={16} /> : 'Laat Astra leren (Studio)'}
              </Button>
            )}>
              Nieuwe bronnen worden op de Mac Studio door Astra geopend in After Effects en Illustrator. De learnings die
              daaruit komen, neem je daarna met één klik over in de skill hiernaast.
            </Alert>
          )}
        </Box>

        {/* Rechts: skill per klant */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">Werkwijze (skill){klant ? ` voor ${klant.naam}` : ''}</Typography>
            {skill && (
              <Chip size="small" variant="outlined" icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                label={`v${skill.versie} · ${skill.bron}${skill.bijgewerktDoor ? ` · ${skill.bijgewerktDoor.split('@')[0]}` : ''}`} />
            )}
            <Box sx={{ flex: 1 }} />
            {voorstellen.map((v) => (
              <Tooltip key={v.naam} title={v.beschrijving || ''}>
                <Button size="small" startIcon={<AutoAwesomeIcon />} disabled={!klant} onClick={() => importeerVoorstel(v.naam)} sx={{ textTransform: 'none' }}>
                  Voorstel: {v.naam}
                </Button>
              </Tooltip>
            ))}
          </Box>
          <TextField multiline minRows={22} maxRows={40} fullWidth value={skillTekst} disabled={!klant}
            onChange={(e) => setSkillTekst(e.target.value)}
            placeholder={klant ? `# ${klant.naam}-animatie\n\nRegels voor beweging, timing, overgangen, typografie…` : 'Kies een klant'}
            InputProps={{ sx: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, lineHeight: 1.5 } }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
            <Button variant="contained" startIcon={opslaan ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={!klant || !gewijzigd || opslaan || !skillTekst.trim()} onClick={() => bewaarSkill('mens')} sx={{ textTransform: 'none' }}>
              Opslaan als nieuwe versie
            </Button>
            {gewijzigd && <Typography variant="caption" color="text.secondary">Niet opgeslagen wijzigingen</Typography>}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Deze tekst gaat als <code>skills/&lt;klant&gt;/SKILL.md</code> mee in het bouwpakket van elke animatie-job voor deze klant.
            Schrijf regels die te toetsen zijn: wat beweegt, hoe (opbouw, easing, tempo), overgangen, typografie, wat nooit mag.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
